import { landmineNames } from '@mm/constants/landmine-names';
import { useConnectionCRUDActions } from '@mm/state/actions/ConnectionsCRUDActions';
import { useKlaviyoCRUDActions } from '@mm/state/actions/KlaviyoCRUDActions';
import { selectKlaviyoConnections } from '@mm/state/modules/connectionsTableState/selectors';
import { activeFacebookPageIdSelector } from '@mm/state/selectors/viewSelectors';
import { StoreState } from '@mm/state/store-interfaces';
import { useGuardedConnectOAuthToken } from '@mm/stateful-components/OAuth/use-guarded-connect-o-auth-token';
import { useCallback } from 'react';
import { useSelector } from 'react-redux';

export const useConnectKlaviyo = () => {
  const facebookPageId = useSelector(activeFacebookPageIdSelector);
  const accounts = useSelector((state: StoreState) => state.klaviyo.accounts);
  const apiKeys = useSelector((state: StoreState) => state.klaviyo.apiKeys);
  const klaviyoConnections = useSelector(selectKlaviyoConnections);

  const {
    getOauthUrl,
    getAccounts,
    getApiKeys,
    getLists,
    getRevenues,
    revoke: revokeToken,
    createApiKey,
    updateApiKey,
  } = useKlaviyoCRUDActions();
  const { retrieveAll, update, create } = useConnectionCRUDActions();

  const fetchOAuthUrl = useCallback(
    async (tokenId?: number) => {
      const link = await getOauthUrl(tokenId);
      return link;
    },
    [getOauthUrl],
  );

  const updateKey = async (id: number, privateKey: string) => {
    await updateApiKey(id, privateKey);
    getLists(facebookPageId, id, 'api_key', true);
  };

  const createKey = async (privateKey: string) => {
    const { id } = await createApiKey(privateKey);
    getLists(facebookPageId, id, 'api_key', true);
    if (!klaviyoConnections.length) {
      create(facebookPageId, {
        name: 'Klaviyo Connection',
        connectionKind: 'klaviyo',
        klaviyoParams: { accessTokenId: id },
      });
    } else if (
      (klaviyoConnections || []).find(
        conn => conn.klaviyoAccessToken?.id !== id,
      )
    ) {
      klaviyoConnections.forEach(connection => {
        update(facebookPageId, {
          id: connection.id,
          klaviyoParams: { accessTokenId: id },
        });
      });
    }
  };

  const { connect: connectKlaviyoToken } = useGuardedConnectOAuthToken(
    'nativeIntegrations',
    landmineNames.KLAVIYO_UPGRADE,
    'klaviyoConnect',
    'klaviyoTokenReceived',
    fetchOAuthUrl,
    async () => {
      const accountsResponse = await getKlaviyoAccounts();
      const { id } = accountsResponse[0];
      getLists(facebookPageId, id, 'oauth_token', true);
      if (!klaviyoConnections.length) {
        create(facebookPageId, {
          name: 'Klaviyo Connection',
          connectionKind: 'klaviyo',
          klaviyoParams: { oauthTokenId: id },
        });
      } else if (
        (klaviyoConnections || []).find(
          conn => conn.klaviyoOauthToken?.id !== id,
        )
      ) {
        klaviyoConnections.forEach(connection => {
          update(facebookPageId, {
            id: connection.id,
            klaviyoParams: { oauthTokenId: id },
          });
        });
      }
    },
    { width: 600, height: 600 },
  );

  const getKlaviyoAccounts = async () => getAccounts(facebookPageId);
  const getKlaviyoApiKeys = async () => getApiKeys(facebookPageId);

  const getKlaviyoConnections = () => {
    retrieveAll(facebookPageId, {
      type: 'klaviyo',
      pageSize: 200,
    });
  };

  const revoke = async (id: number) => {
    const result = await revokeToken(id);
    if (result) {
      getKlaviyoConnections();
    }
  };

  const refreshConnections = async () => {
    retrieveAll(facebookPageId, {
      type: 'klaviyo',
      pageSize: 200,
    });
  };

  return {
    accounts,
    apiKeys,
    getRevenues,
    getLists,
    getKlaviyoAccounts,
    getKlaviyoApiKeys,
    refreshConnections,
    klaviyoConnections,
    getKlaviyoConnections,
    connectKlaviyoToken,
    revoke,
    createApiKey: createKey,
    updateApiKey: updateKey,
  };
};
